/**
 * Module summary:
 * This page collects the minimum site details needed to open a new assessment.
 * It validates the required fields, creates the local/backend record, and then
 * routes the user straight into the assessment workspace.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../layout/AppShell';
import { createAssessment, listAssessments, listAssessmentsByUser } from '../../services/data';
import {
  ACTIVE_ASSESSMENT_ID,
  ACTIVE_ASSESSMENT_TITLE,
  ACTIVE_ASSESSMENT_VERSION,
} from '../../services/data/assessmentCatalog';
import {
  createGoogleAutocompleteSessionToken,
  fetchGoogleAddressSuggestions,
  type GoogleAddressSuggestion,
} from '../../services/googleMaps';
import { useAuth } from '../../hooks/useAuth';
import { INPUT_LIMITS, sanitizeSingleLineText } from '../../utils/inputSecurity';
import './pages.css';

const buildingTypes = [
  // Offer a curated list so the building type stays consistent across reports and filtering.
  'School / University',
  'Office Building',
  'Government Facility',
  'Hospital / Healthcare',
  'Warehouse / Industrial',
  'Retail / Commercial',
  'Residential Complex',
  'Other'
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY',
  'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH',
  'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
] as const;

type AddressForm = {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
};

type AddressSuggestion = AddressForm & {
  id: string;
  label: string;
  searchIndex: string;
  source: 'google' | 'saved';
  primaryText?: string;
  secondaryText?: string;
  resolve?: () => Promise<AddressForm | null>;
};

function composeAddress(parts: AddressForm): string {
  return [parts.streetAddress, parts.city, parts.state, parts.zipCode].filter(Boolean).join(', ');
}

function parseAddress(value: string): AddressForm | null {
  const cleaned = sanitizeSingleLineText(value, INPUT_LIMITS.assessmentAddress);
  if (!cleaned) return null;

  const pieces = cleaned
    .split(',')
    .map((piece) => piece.trim())
    .filter(Boolean);

  if (pieces.length === 0) return null;

  const streetAddress = pieces[0] || '';
  const city = pieces.length > 1 ? pieces[1] || '' : '';
  const tail = pieces.slice(2).join(' ').trim();
  const tailMatch = tail.match(/\b([A-Za-z]{2})\b(?:\s+(\d{5}(?:-\d{4})?))?$/);

  let state = '';
  let zipCode = '';

  if (tailMatch) {
    state = tailMatch[1].toUpperCase();
    zipCode = tailMatch[2] || '';
  } else if (pieces.length > 2) {
    const potentialState = sanitizeSingleLineText(pieces[2], 2).toUpperCase();
    if (US_STATES.includes(potentialState as typeof US_STATES[number])) {
      state = potentialState;
    }
    if (pieces.length > 3) {
      zipCode = sanitizeSingleLineText(pieces[3], 10);
    }
  }

  return {
    streetAddress,
    city,
    state,
    zipCode,
  };
}

function buildSuggestions(addresses: string[]): AddressSuggestion[] {
  const seen = new Set<string>();

  return addresses
    .map((address) => parseAddress(address))
    .filter((entry): entry is AddressForm => Boolean(entry?.streetAddress))
    .map((entry) => {
      const label = composeAddress(entry);
      return {
        ...entry,
        id: label,
        label,
        searchIndex: label.toLowerCase(),
        source: 'saved' as const,
      };
    })
    .filter((entry) => {
      if (!entry.label || seen.has(entry.label)) return false;
      seen.add(entry.label);
      return true;
    });
}

export const CreateAssessment: React.FC = () => {
  const [name, setName] = useState('');
  const [buildingType, setBuildingType] = useState('');
  const [address, setAddress] = useState<AddressForm>({
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [googleSuggestions, setGoogleSuggestions] = useState<AddressSuggestion[]>([]);
  const [error, setError] = useState('');
  const [assessmentId] = useState(ACTIVE_ASSESSMENT_ID);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const autocompleteSessionRef = useRef<any | null>(null);

  const availableAssessments = useMemo(
    () => (user?.id ? listAssessmentsByUser(user.id) : listAssessments()),
    [user?.id]
  );

  const savedAddressSuggestions = useMemo(
    () => buildSuggestions(availableAssessments.map((assessment) => assessment.address || '')),
    [availableAssessments]
  );

  const composedAddress = useMemo(() => composeAddress(address), [address]);
  const addressQuery = address.streetAddress.trim().toLowerCase();
  const filteredSuggestions = useMemo(() => {
    const savedMatches = !addressQuery
      ? savedAddressSuggestions.slice(0, 5)
      : savedAddressSuggestions.filter((suggestion) => suggestion.searchIndex.includes(addressQuery)).slice(0, 5);

    if (googleSuggestions.length > 0) {
      return [...googleSuggestions, ...savedMatches.filter((saved) => !googleSuggestions.some((google) => google.label === saved.label))].slice(0, 6);
    }

    return savedMatches;
  }, [addressQuery, googleSuggestions, savedAddressSuggestions]);

  useEffect(() => {
    let cancelled = false;
    const streetAddress = address.streetAddress.trim();

    if (!streetAddress || streetAddress.length < 3) {
      setGoogleSuggestions([]);
      setLoadingSuggestions(false);
      autocompleteSessionRef.current = null;
      return () => {
        cancelled = true;
      };
    }

    const timer = window.setTimeout(() => {
      void (async () => {
        setLoadingSuggestions(true);
        try {
          if (!autocompleteSessionRef.current) {
            autocompleteSessionRef.current = await createGoogleAutocompleteSessionToken();
          }

          const suggestions = await fetchGoogleAddressSuggestions(streetAddress, autocompleteSessionRef.current);
          if (cancelled) return;

          const mapped: AddressSuggestion[] = suggestions.map((suggestion: GoogleAddressSuggestion) => ({
            id: suggestion.id,
            streetAddress: suggestion.primaryText,
            city: '',
            state: '',
            zipCode: '',
            label: suggestion.label,
            searchIndex: suggestion.label.toLowerCase(),
            source: 'google',
            primaryText: suggestion.primaryText,
            secondaryText: suggestion.secondaryText,
            resolve: async () => {
              const details = await suggestion.toAddressParts();
              if (!details) return null;
              return {
                streetAddress: details.streetAddress,
                city: details.city,
                state: details.state,
                zipCode: details.zipCode,
              };
            },
          }));

          setGoogleSuggestions(mapped);
        } catch {
          if (!cancelled) {
            setGoogleSuggestions([]);
          }
        } finally {
          if (!cancelled) {
            setLoadingSuggestions(false);
          }
        }
      })();
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [address.streetAddress]);

  const isValid = Boolean(
    !authLoading &&
    user?.id &&
    name.trim() &&
    address.streetAddress.trim() &&
    address.city.trim() &&
    address.state.trim() &&
    address.zipCode.trim() &&
    buildingType
  );
  // Require the key setup fields before allowing a new assessment to start.

  const updateAddress = (field: keyof AddressForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const nextValue = field === 'state'
      ? sanitizeSingleLineText(e.target.value, 2).toUpperCase()
      : e.target.value;

    setAddress((current) => ({ ...current, [field]: nextValue }));
    if (field === 'streetAddress' || field === 'city' || field === 'zipCode') {
      setShowSuggestions(true);
    }
  };

  const applySuggestion = async (suggestion: AddressSuggestion) => {
    try {
      const resolved = suggestion.resolve ? await suggestion.resolve() : {
        streetAddress: suggestion.streetAddress,
        city: suggestion.city,
        state: suggestion.state,
        zipCode: suggestion.zipCode,
      };

      if (!resolved) return;

      setAddress({
        streetAddress: resolved.streetAddress,
        city: resolved.city,
        state: resolved.state,
        zipCode: resolved.zipCode,
      });
      autocompleteSessionRef.current = null;
      setGoogleSuggestions([]);
      setShowSuggestions(false);
      setError('');
    } catch {
      setError('Could not load that address. Please try another result.');
    }
  };

  const onCreate = async () => {
    if (!isValid) return;

    if (!user?.id) {
      setError('Your session is still loading. Please wait a moment and try again.');
      return;
    }

    const sanitizedName = sanitizeSingleLineText(name, INPUT_LIMITS.assessmentName);
    const streetAddress = sanitizeSingleLineText(address.streetAddress, INPUT_LIMITS.address);
    const city = sanitizeSingleLineText(address.city, INPUT_LIMITS.city);
    const state = sanitizeSingleLineText(address.state, 2).toUpperCase();
    const zipCode = sanitizeSingleLineText(address.zipCode, 10);
    const normalizedBuildingType = buildingTypes.includes(buildingType) ? buildingType : '';
    const sanitizedAddress = composeAddress({ streetAddress, city, state, zipCode });

    if (!sanitizedName || !streetAddress || !city || !state || !zipCode || !normalizedBuildingType) {
      // Stop creation when required fields collapse to empty or the building type is tampered with.
      setError('Enter a valid assessment name, facility address, and building type.');
      return;
    }

    if (!US_STATES.includes(state as typeof US_STATES[number])) {
      setError('Please select a valid US state.');
      return;
    }

    if (!/^\d{5}(?:-\d{4})?$/.test(zipCode)) {
      setError('Please enter a valid ZIP code.');
      return;
    }

    setError('');

    try {
      const rec = await createAssessment({
        // Convert the chosen building type into a simple slug so it can double as a stable building id.
        name: sanitizedName,
        buildingId: normalizedBuildingType.toLowerCase().replace(/[\s/]+/g, '-'),
        assessmentId,
        assessmentVersion: ACTIVE_ASSESSMENT_VERSION,
        assessmentTitle: ACTIVE_ASSESSMENT_TITLE,
        address: sanitizedAddress,
        addressParts: {
          street: streetAddress,
          city,
          state,
          zipCode,
        },
        buildingType: normalizedBuildingType,
        userId: user?.id
      });

      // Navigate directly into the new assessment so the user can begin working immediately.
      navigate(`/assessment?id=${encodeURIComponent(rec.id)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the assessment.');
    }
  };

  return (
    <AppShell title="New Assessment" isDashboard={true}>
      <div className="create-assessment-page">
        <div className="create-assessment-page__shell">
          <div className="create-assessment-page__intro">
            <p className="create-assessment-page__eyebrow">Assessment Setup</p>
            <h1 className="create-assessment-page__title">Start a new security assessment</h1>
            <p className="create-assessment-page__subtitle">
              Add the facility details below to open a new walkthrough and begin scoring the site.
            </p>
            <div className="create-assessment-page__highlights">
              <div className="create-assessment-page__highlight">
                <span className="create-assessment-page__highlight-label">Workflow</span>
                <strong className="create-assessment-page__highlight-value">Guided walkthrough</strong>
              </div>
              <div className="create-assessment-page__highlight">
                <span className="create-assessment-page__highlight-label">Output</span>
                <strong className="create-assessment-page__highlight-value">Report-ready findings</strong>
              </div>
            </div>
          </div>

          <div className="create-assessment-card">
            <div className="create-assessment-card__accent" />

            <div className="create-assessment-card__header">
              <div className="create-assessment-card__icon">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>

              <div className="create-assessment-card__heading">
                <h2 className="create-assessment-card__title">New Assessment</h2>
                <p className="create-assessment-card__subtitle">
                  Enter the facility details below to begin the walkthrough.
                </p>
              </div>
            </div>

            <div className="create-assessment-form">
              {error && <p className="create-account__error">{error}</p>}
              <div className="create-assessment-field">
                <label className="create-assessment-field__label">
                  Assessment Name <span>*</span>
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Westfield Elementary Security Review"
                  className="create-assessment-field__input"
                />
              </div>

              <div className="create-assessment-field">
                <label className="create-assessment-field__label">
                  Facility Address <span>*</span>
                </label>
                <div className="create-assessment-address-search">
                  <input
                    value={address.streetAddress}
                    onChange={updateAddress('streetAddress')}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => window.setTimeout(() => setShowSuggestions(false), 120)}
                    placeholder="Street address"
                    className="create-assessment-field__input"
                    autoComplete="street-address"
                  />
                  {showSuggestions && filteredSuggestions.length > 0 && (
                    <div className="create-assessment-suggestions" role="listbox" aria-label="Address suggestions">
                      {filteredSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          type="button"
                          className="create-assessment-suggestions__item"
                          onMouseDown={() => void applySuggestion(suggestion)}
                        >
                          <strong>{suggestion.primaryText || suggestion.streetAddress}</strong>
                          <span>{suggestion.secondaryText || `${suggestion.city}, ${suggestion.state} ${suggestion.zipCode}`.trim()}</span>
                          <em>{suggestion.source === 'google' ? 'Google address match' : 'Saved assessment address'}</em>
                        </button>
                      ))}
                      <div className="create-assessment-suggestions__footer">
                        {loadingSuggestions ? 'Searching Google addresses...' : 'Powered by Google and your saved assessments'}
                      </div>
                    </div>
                  )}
                </div>
                <p className="create-assessment-field__hint">
                  Browser autofill is enabled, and live Google address matches appear as you type.
                </p>
                <div className="create-assessment-address-grid">
                  <input
                    value={address.city}
                    onChange={updateAddress('city')}
                    placeholder="City"
                    className="create-assessment-field__input"
                    autoComplete="address-level2"
                  />
                  <select
                    value={address.state}
                    onChange={updateAddress('state')}
                    className="create-assessment-field__input create-assessment-field__select"
                    autoComplete="address-level1"
                  >
                    <option value="">State</option>
                    {US_STATES.map((stateOption) => (
                      <option key={stateOption} value={stateOption}>{stateOption}</option>
                    ))}
                  </select>
                  <input
                    value={address.zipCode}
                    onChange={updateAddress('zipCode')}
                    placeholder="ZIP code"
                    className="create-assessment-field__input"
                    autoComplete="postal-code"
                    inputMode="numeric"
                  />
                </div>
              </div>

              <div className="create-assessment-field">
                <label className="create-assessment-field__label">
                  Building Type <span>*</span>
                </label>
                <select
                  value={buildingType}
                  onChange={(e) => setBuildingType(e.target.value)}
                  className="create-assessment-field__input create-assessment-field__select"
                >
                  <option value="" disabled>Select building type...</option>
                  {buildingTypes.map((bt) => (
                    <option key={bt} value={bt}>{bt}</option>
                  ))}
                </select>
              </div>

              {isValid && (
                <div className="create-assessment-summary">
                  <div className="create-assessment-summary__header">
                    <div className="create-assessment-summary__badge">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <p className="create-assessment-summary__title">Ready to begin</p>
                  </div>
                  <p className="create-assessment-summary__copy">
                    <strong>{name}</strong>
                    <br />
                    <span>{buildingType} at {composedAddress}</span>
                  </p>
                </div>
              )}

              <button
                onClick={onCreate}
                disabled={!isValid}
                className={`create-assessment-submit ${
                  isValid
                    ? 'create-assessment-submit--active'
                    : 'create-assessment-submit--disabled'
                }`}
              >
                Begin Security Assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default CreateAssessment;
